import {DropdownMenu} from '@wandb/components';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import {isEqual} from 'lodash';
import React, {
  type FC,
  memo,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import {Popup} from 'semantic-ui-react';
import {Editor, type Node, Transforms} from 'slate';
import {ReactEditor, useSlateStatic} from 'slate-react';

import {getParentReportURL} from '../../../../util/report';
import {useTyping} from '../../TypingContext';
import * as S from './drag-drop.styles';
import {duplicateNode} from './utils';
import {WBSlateDragDropContext} from './WBSlateDragDropContext';

interface BlockDragHandleProps {
  handleAttributes: any;
  element: Node;
  deepLinkId?: string;
}

const BlockDragHandleComp: FC<BlockDragHandleProps> = ({
  handleAttributes,
  element,
  deepLinkId,
}) => {
  const {typing} = useTyping();
  const editor = useSlateStatic();
  const {parentViewId, dragItem, dragHandleMenuOpen, setDragHandleMenuOpen} =
    useContext(WBSlateDragDropContext);

  const dragging = dragItem != null;

  const triggerRef = useRef<HTMLDivElement>(null);
  // Every block mounts its own handle, so the shared context flag can't say
  // which handle is open; it only suppresses the tooltip while a menu is open.
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setMenuOpen(isOpen);
      setDragHandleMenuOpen(isOpen);
    },
    [setDragHandleMenuOpen]
  );

  const duplicateBlock = useCallback(() => {
    const path = ReactEditor.findPath(editor, element);
    const copy = duplicateNode(Editor.node(editor, path)[0]);
    Transforms.insertNodes(editor, copy, {at: path});
    Transforms.deselect(editor);
    handleOpenChange(false);
  }, [editor, element, handleOpenChange]);

  const deleteBlock = useCallback(() => {
    Transforms.removeNodes(editor, {
      at: ReactEditor.findPath(editor, element),
    });
    Transforms.deselect(editor);
    handleOpenChange(false);
  }, [editor, element, handleOpenChange]);

  const copyDeepLink = useCallback(() => {
    const deepUrl =
      getParentReportURL(window.location.href, parentViewId) + '#' + deepLinkId;
    copyToClipboard(deepUrl);
    toast('Copied link');
    Transforms.deselect(editor);
    handleOpenChange(false);
  }, [deepLinkId, editor, handleOpenChange, parentViewId]);

  const offsetTop =
    element.type === 'heading' && (element as any).level === 1 ? 4 : undefined;

  if (dragging) {
    return isEqual(dragItem, element) ? (
      // Drag handle without popups (displayed on element while actively dragging)
      <S.DragHandle
        offsetTop={offsetTop}
        contentEditable={false}
        hidden={typing}
        {...handleAttributes}>
        <Icon name="drag-grip" />
      </S.DragHandle>
    ) : (
      // Disable drag handle if you're dragging a different element
      <></>
    );
  }

  return (
    // Drag handle with popup menus
    <>
      <Popup
        size="tiny"
        position="top center"
        basic
        disabled={dragHandleMenuOpen}
        inverted
        trigger={
          <S.DragHandle
            ref={triggerRef}
            forceVisible={menuOpen}
            offsetTop={offsetTop}
            onClick={() => handleOpenChange(!menuOpen)}
            contentEditable={false}
            hidden={typing}
            {...handleAttributes}>
            <Icon name="drag-grip" />
          </S.DragHandle>
        }
        content={
          <div>
            Drag to move
            <br />
            Click to open menu
          </div>
        }
      />
      <DropdownMenu.Content
        isOpen={menuOpen}
        onOpenChange={handleOpenChange}
        triggerRef={triggerRef}
        placement="left">
        <DropdownMenu.Item onAction={duplicateBlock}>
          <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'copy'}} />
          <DropdownMenu.ItemLabel>Duplicate</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
        <DropdownMenu.Item onAction={deleteBlock}>
          <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'delete'}} />
          <DropdownMenu.ItemLabel>Delete</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
        {deepLinkId != null && (
          <DropdownMenu.Item onAction={copyDeepLink}>
            <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'link-alt'}} />
            <DropdownMenu.ItemLabel>Copy link</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </>
  );
};

export const BlockDragHandle = memo(BlockDragHandleComp);
